'use client';

import { useState } from 'react';

import { CheckCircle, Minus, XCircle } from 'lucide-react';

import { BenefitsSection } from '@/components/sections/benefits-section';
import { CustomerVideos } from '@/components/sections/customer-videos';
import { FaqSection } from '@/components/sections/faq-section';
import {
  BubbleAutopilot,
  SubpageHero,
} from '@/components/sections/subpage-hero';
import { TemplatesList } from '@/components/sections/templates-list';
import Testimonials from '@/components/sections/testimonials';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useTranslation } from '@/lib/i18n/context';
import { Link } from '@/lib/i18n/link';
import { cn } from '@/lib/utils';

type ComparisonStatus = 'best' | 'worst' | 'neutral';

interface ComparisonRow {
  metric: string;
  agent5: { value: string; status: ComparisonStatus };
  others: { value: string; status: ComparisonStatus };
}

const CONTENT = {
  en: {
    eyebrow: 'Helpdesks',
    heroTitle: 'AI-native helpdesk built for happy reps',
    heroSubtitleA: 'Autopilot handles the busywork.',
    heroSubtitleB: 'Your team gets back to real conversations — from anywhere.',
    autopilotTitle: 'Autopilot · ticket #4521',
    autopilotSteps: [
      'Classified intent · Refund request',
      'Pulled order from CRM',
      'Verified eligibility · 12 days old',
      'Refund issued · $89.00',
      'Replied to customer · closed',
    ],
  },
  ar: {
    eyebrow: 'أنظمة الدعم',
    heroTitle: 'نظام دعم ذكي من الأساس — مبني عشان يرتاح فريقك',
    heroSubtitleA: 'Autopilot يتولّى المهام الروتينية.',
    heroSubtitleB: 'وفريقك يرجع للمحادثات الحقيقية — من أي مكان.',
    autopilotTitle: 'Autopilot · تذكرة #4521',
    autopilotSteps: [
      'صنّف نوع الطلب · استرداد',
      'سحب الطلب من الـ CRM',
      'تأكّد من الأهلية · عمر الطلب ١٢ يوم',
      'رجّع المبلغ · $89.00',
      'ردّ على العميل · وأقفل',
    ],
  },
} as const;

export default function OpenPage() {
  const { t, locale } = useTranslation();
  const c = locale === 'ar' ? CONTENT.ar : CONTENT.en;
  const [hoveredModel, setHoveredModel] = useState<string | null>(null);

  const comparisonData: ComparisonRow[] = [
    {
      metric: t.helpdesk['Helpdesk Included'],
      agent5: { value: t.helpdesk['Built-in'], status: 'best' },
      others: { value: t.helpdesk['Separate purchase'], status: 'worst' },
    },
    {
      metric: t.helpdesk['AI Agent'],
      agent5: { value: t.helpdesk['Agent 5 (best in class)'], status: 'best' },
      others: { value: t.helpdesk['Limited or none'], status: 'worst' },
    },
    {
      metric: t.helpdesk['Workflows'],
      agent5: { value: t.helpdesk['Native automation'], status: 'best' },
      others: { value: t.helpdesk['Third-party tools'], status: 'worst' },
    },
    {
      metric: t.helpdesk['All-in-One'],
      agent5: { value: t.helpdesk['Complete platform'], status: 'best' },
      others: { value: t.helpdesk['Multiple tools'], status: 'worst' },
    },
    {
      metric: t.helpdesk['Setup Time'],
      agent5: { value: t.helpdesk['5 minutes'], status: 'best' },
      others: { value: t.helpdesk['Days/weeks'], status: 'worst' },
    },
    {
      metric: t.helpdesk['Observability'],
      agent5: { value: t.helpdesk['Full transparency'], status: 'best' },
      others: { value: t.helpdesk['Limited insights'], status: 'worst' },
    },
    {
      metric: t.helpdesk['AI Training'],
      agent5: { value: t.helpdesk['Built-in with evals'], status: 'best' },
      others: { value: t.helpdesk['Manual or none'], status: 'worst' },
    },
    {
      metric: t.helpdesk['Integration Needed'],
      agent5: { value: t.helpdesk['None'], status: 'best' },
      others: { value: t.helpdesk['Multiple integrations'], status: 'worst' },
    },
  ];

  return (
    <>
      <SubpageHero
        eyebrow={c.eyebrow}
        title={c.heroTitle}
        subtitle={
          <>
            {c.heroSubtitleA}
            <br />
            {c.heroSubtitleB}
          </>
        }
        videoSrc="/home/features/open-helpdesk.mp4"
        worksWith={null}
        bubblePosition="bottom-left"
        bubbles={[
          {
            role: 'agent',
            widget: (
              <BubbleAutopilot
                title={c.autopilotTitle}
                steps={[...c.autopilotSteps]}
              />
            ),
          },
        ]}
      />

      <CustomerVideos />

      <BenefitsSection
        title={t.helpdesk['Everything you need, built-in']}
        subtitle={
          t.helpdesk['Helpdesk, AI agent, workflows—all in one super app.']
        }
        learnMoreLink="#comparison"
        accentColor="emerald"
        theme="light"
        benefits={[
          {
            title: t.helpdesk['Complete platform, no integrations'],
            description:
              t.helpdesk[
                'Built-in helpdesk, Agent 5 AI engine, workflows, and observability. Everything you need in one place.'
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-3.svg',
            imagePosition: 'right',
          },
          {
            title: t.helpdesk['Set up in minutes'],
            description:
              t.helpdesk[
                'No complex integrations, no setup headaches. Just start using it.'
              ],
            linkText: '',
            linkHref: '#',
            imageUrl:
              'https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-3.svg',
            imagePosition: 'left',
          },
          {
            title: t.helpdesk['Scale with confidence'],
            description:
              t.helpdesk[
                'From startup to enterprise, Open grows with you. All the features you need, no vendor lock-in.'
              ],
            linkText: '',
            linkHref: '#',
            imagePosition: 'right',
            statCard: {
              value: '77%',
              label: t.helpdesk['Automation Rate'],
              sublabel: t.helpdesk['Industry-leading performance'],
            },
          },
        ]}
      />

      {/* Comparison Section */}
      <section className="bg-white py-32">
        <div className="container mx-auto">
          <div className="mx-auto mb-16 flex max-w-3xl flex-col justify-center gap-7 text-center md:text-center">
            <h2 className="font-serif text-2xl md:text-4xl">
              {t.helpdesk['Open vs Fragmented Tools']}
            </h2>
            <p className="text-muted-foreground text-sm md:text-base">
              {
                t.helpdesk[
                  "See how Open's all-in-one platform compares to piecing together multiple tools."
                ]
              }
            </p>
          </div>
          <div className="relative overflow-hidden p-8">
            <div className="border-border/50 bg-background/50 relative overflow-hidden border backdrop-blur-sm">
              <Table>
                <TableHeader>
                  <TableRow className="border-border/50">
                    <TableHead className="text-foreground font-semibold">
                      {t.helpdesk['Feature']}
                    </TableHead>
                    <TableHead className="text-foreground text-center font-semibold">
                      {t.helpdesk['Open (All-in-One)']}
                    </TableHead>
                    <TableHead className="text-foreground text-center font-semibold">
                      {t.helpdesk['Multiple Tools']}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {comparisonData.map((row, index) => (
                    <TableRow
                      key={index}
                      className="border-border/30 hover:bg-muted/30 transition-colors"
                    >
                      <TableCell className="text-foreground py-4 font-medium">
                        {row.metric}
                      </TableCell>
                      <TableCell
                        className={cn(
                          'cursor-pointer py-4 text-center font-medium transition-all duration-300',
                          row.agent5.status === 'best'
                            ? 'bg-green-50 text-green-600 dark:bg-green-950/20'
                            : row.agent5.status === 'worst'
                              ? 'bg-red-50 text-red-600 dark:bg-red-950/20'
                              : 'bg-muted/50 text-foreground',
                          hoveredModel === 'agent5' &&
                            'bg-green-50/80 dark:bg-green-950/30',
                        )}
                        onMouseEnter={() => setHoveredModel('agent5')}
                        onMouseLeave={() => setHoveredModel(null)}
                      >
                        <div className="flex items-center justify-center gap-2">
                          {row.agent5.status === 'best' && (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          )}
                          {row.agent5.status === 'worst' && (
                            <XCircle className="h-4 w-4 text-red-600" />
                          )}
                          {row.agent5.status === 'neutral' && (
                            <Minus className="text-muted-foreground h-4 w-4" />
                          )}
                          <span>{row.agent5.value}</span>
                        </div>
                      </TableCell>
                      <TableCell
                        className={cn(
                          'cursor-pointer py-4 text-center font-medium transition-all duration-300',
                          row.others.status === 'best'
                            ? 'bg-green-50 text-green-600 dark:bg-green-950/20'
                            : row.others.status === 'worst'
                              ? 'bg-red-50 text-red-600 dark:bg-red-950/20'
                              : 'bg-muted/50 text-foreground',
                          hoveredModel === 'others' &&
                            'bg-red-50/80 dark:bg-green-950/30',
                        )}
                        onMouseEnter={() => setHoveredModel('others')}
                        onMouseLeave={() => setHoveredModel(null)}
                      >
                        <div className="flex items-center justify-center gap-2">
                          {row.others.status === 'best' && (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          )}
                          {row.others.status === 'worst' && (
                            <XCircle className="h-4 w-4 text-red-600" />
                          )}
                          {row.others.status === 'neutral' && (
                            <Minus className="text-muted-foreground h-4 w-4" />
                          )}
                          <span>{row.others.value}</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </section>

      {/* Automate Open Manual Work Section */}
      <section className="bg-neutral-50 py-32">
        <div className="container mx-auto">
          <div className="mx-auto mb-16 flex max-w-3xl flex-col justify-center gap-7 text-center md:text-center">
            <h2 className="font-serif text-2xl md:text-4xl">
              {t.helpdesk['Automate the manual tasks, not just the answers']}
            </h2>
            <p className="text-muted-foreground text-sm md:text-base">
              {
                t.helpdesk[
                  'In addition to fully resolving support tickets, Open automates the manual tasks that slow down your team—routing, tagging, QA, and more.'
                ]
              }
            </p>
          </div>
          <TemplatesList
            systemFilter="Front"
            categoryFilter="Customer Support"
            limit={6}
            variant="slider"
          />
        </div>
      </section>

      {/* FAQ Section */}
      <FaqSection
        title={t.helpdesk['Frequently asked questions']}
        subtitle={
          t.helpdesk[
            "Find answers to common questions about Open. Can't find what you're looking for? Contact our support team."
          ]
        }
        items={[
          {
            question: t.helpdesk['What is Open?'],
            answer:
              t.helpdesk[
                "Open is the super AI communication app—a complete platform that combines a modern helpdesk, Agent 5 (the world's best AI agent), intelligent workflows, observability tools, and powerful automation in one unified system."
              ],
          },
          {
            question:
              t.helpdesk['What makes Open different from other helpdesks?'],
            answer:
              t.helpdesk[
                "Open is built AI-first. While other platforms bolt on AI as an afterthought, Open is designed from the ground up with Agent 5 at its core, providing seamless AI-human collaboration that others can't match."
              ],
          },
          {
            question: t.helpdesk['What is Agent 5?'],
            answer:
              t.helpdesk[
                "Agent 5 is the core AI engine powering Open. It's a multi-model AI agent with advanced training, observability, compliance features, and continuous learning capabilities—designed to handle complex customer conversations with human-level understanding."
              ],
          },
          {
            question: t.helpdesk['What channels does Open support?'],
            answer:
              t.helpdesk[
                'Open supports all communication channels: Voice AI, web chat, email, WhatsApp, social media, SMS, and Slack. Your customers can reach you anywhere, and Agent 5 responds consistently across all channels.'
              ],
          },
          {
            question: t.helpdesk['What are workflows in Open?'],
            answer:
              t.helpdesk[
                'Open workflows include an agentic engine for intelligent routing, outbound sequencing for proactive engagement, customer insights for data-driven decisions, and knowledge gap detection to continuously improve your support quality.'
              ],
          },
          {
            question:
              t.helpdesk['Do I need to integrate multiple tools with Open?'],
            answer:
              t.helpdesk[
                'No. Open is an all-in-one platform. Everything—helpdesk, AI agent, workflows, training, observability, and compliance—is built-in and works together seamlessly. No third-party integrations needed.'
              ],
          },
          {
            question:
              t.helpdesk['What observability features does Open provide?'],
            answer:
              t.helpdesk[
                "Open provides full transparency into your AI agent's performance with real-time observability, detailed analytics, conversation insights, and evaluation metrics—so you always know how your AI is behaving."
              ],
          },
          {
            question:
              t.helpdesk['Can I use Open if I already have a helpdesk?'],
            answer:
              t.helpdesk[
                'Yes. While Open is a complete platform, you can also use Agent 5 with your existing helpdesk (Zendesk, Intercom, Salesforce, etc.). But experiencing everything together in Open gives you the best results.'
              ],
          },
        ]}
      />

      {/* Testimonials Section */}
      <Testimonials />
    </>
  );
}
